import {Injectable} from '@angular/core';
import {ConsentBasicModel} from '@core/modules/rest/consent/response/consent-basic.model';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {UnitStatusEnum} from '@core/modules/rest/consent/enums/unit-status.enum';
import {OutlineChapterUnitBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-basic.model';
import {filter, take} from 'rxjs/operators';
import {FileRestService} from '@core/modules/rest/file/file-rest.service';

@Injectable({
    providedIn: 'root'
})
export class UnitInformationService {

    constructor(private fileRestService: FileRestService) {
    }

    getReadUnitsFromInitialConsent(consent: OutlineUnitBasicModel[]): number {
        let counter = 0;
        consent.forEach(unit => {
            if (unit.status === UnitStatusEnum.UNDERSTOOD) {
                counter++;
            }
        });
        return counter;
    }

    getReadUnitCountFromConsent(consent: ConsentBasicModel): number {
        let counter = 0;
        consent.consentContent.forEach(chapter => {
            counter += this.getReadUnitCount(chapter.units);
        });
        return counter;
    }

    getReadUnitCount(units: OutlineUnitBasicModel[]): number {
        let counter = 0;
        units.forEach(unit => {
            if (unit.status === UnitStatusEnum.UNDERSTOOD) {
                counter++;
            }
            if (unit.children.length > 0) {
                counter += this.countReadChildren(unit.children);
            }
        });
        return counter;
    }

    countReadChildren(children: OutlineUnitBasicModel[]): number {
        let counter = 0;
        children.forEach(unit => {
            if (unit.status === UnitStatusEnum.UNDERSTOOD) {
                counter++;
            }
            if (unit.children.length > 0) {
                counter += this.countReadChildren(unit.children);
            }
        });
        return counter;
    }

    getSkippedUnits(chapters: OutlineChapterUnitBasicModel[]): OutlineUnitBasicModel[] {
        const retArray: OutlineUnitBasicModel[] = [];
        chapters.forEach(chapter => {
            chapter.units.forEach(unit => {
                if (unit.skipConfirmationTime) {
                    retArray.push(unit);
                }
                if (unit.children.length > 0) {
                    Array.prototype.push.apply(retArray, this.getSkippedChildren(unit.children));
                }
            });
        });
        return retArray;
    }

    getSkippedChildren(units: OutlineUnitBasicModel[]): OutlineUnitBasicModel[] {
        const arr: OutlineUnitBasicModel[] = [];
        units.forEach(unit => {
            if (unit.skipConfirmationTime) {
                arr.push(unit);
            }
            if (unit.children.length > 0) {
                Array.prototype.push.apply(arr, this.getSkippedChildren(unit.children));
            }
        });
        return arr;
    }

    getInlineImage(images: NodeList): void {
        images.forEach((item: any) => {
            const idAttribute = item.getAttribute('id');
            if (idAttribute != null) {
                this.getImageFromService(idAttribute, item);
            }
        });
    }

    getImageFromService(imageId: string, item: any): void {
        this.fileRestService.getSingleResourceById(imageId).pipe(filter(value => !!value), take(1)).subscribe(response => {
            let binary = '';
            const bytes = new Uint8Array(response);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            const base64String = btoa(binary);
            item.innerHTML = '<img src="' + 'data:image/png;base64,' + base64String + '" alt="image">';
        });
    }

    getInlineVideos(videos: NodeList): void {
        videos.forEach((item: any) => {
            const idAttribute = item.getAttribute('id');
            if (idAttribute != null) {
                this.getVideoFromService(idAttribute, item);
            }
        });
    }

    getVideoFromService(videoId: string, item: any): void {
        this.fileRestService.getSingleResourceById(videoId).pipe(filter(value => !!value), take(1)).subscribe(response => {
            let binary = '';
            const bytes = new Uint8Array(response);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            const base64String = btoa(binary);
            item.innerHTML = '<source src="data:video/mp4;base64,' + base64String + '" type="video/mp4"/>';
        });
    }

    getTotalUnitsFromConsent(consent: ConsentBasicModel): number {
        let counter = 0;
        consent.consentContent.forEach(chapter => {
            counter += this.getUnitCount(chapter.units);
        });
        return counter;
    }

    getUnitCount(units: OutlineUnitBasicModel[]): number {
        let counter = 0;
        units.forEach(unit => {
            counter++;
            if (unit.children.length > 0) {
                counter += this.countChildren(unit.children);
            }
        });
        return counter;
    }

    countChildren(children: OutlineUnitBasicModel[]): number {
        let counter = 0;
        children.forEach(unit => {
            counter++;
            if (unit.children.length > 0) {
                counter += this.countChildren(unit.children);
            }
        });
        return counter;
    }
}
